import { useCallback } from 'react';
import { EditorInstance } from 'novel';

interface UseKeyboardShortcutsOptions {
    editorInstance: EditorInstance | null;
    isAgentMode?: boolean;
    onSave?: () => void;
    onAICommand?: (type: 'generate' | 'refine' | 'summarize-title' | 'generate-image') => void;
    onOpenAiPrompt?: () => void;
}

/**
 * Hook to manage keyboard shortcuts within EditorCore.
 * 
 * Shortcuts:
 * - Cmd+S: Save
 * - Cmd+.: Open inline AI prompt
 * - Cmd+Shift+R: Refine
 * - Cmd+Shift+G: Generate
 * - Cmd+Shift+T: Summarize title
 * - Cmd+Shift+I: Generate image
 */
export function useKeyboardShortcuts({
    editorInstance,
    isAgentMode = false,
    onSave,
    onAICommand,
    onOpenAiPrompt,
}: UseKeyboardShortcutsOptions) {
    const handleKeyboardShortcuts = useCallback((event: KeyboardEvent) => {
        // Most shortcuts only work in agent mode
        const { ctrlKey, metaKey, shiftKey, key } = event;
        const isModifierPressed = ctrlKey || metaKey; // Support both Ctrl and Cmd

        // Ctrl+S - Manual save (works in both modes)
        if (isModifierPressed && key.toLowerCase() === 's') {
            event.preventDefault();
            onSave?.();
            return true;
        }

        // Agent mode only shortcuts
        if (!isAgentMode) return false;

        if (isModifierPressed && key === '.') {
            // Ctrl/Cmd + . - Open inline AI prompt
            event.preventDefault();
            editorInstance?.chain().focus().scrollIntoView().run();
            onOpenAiPrompt?.();
            return true;
        }

        if (isModifierPressed && shiftKey && key.toLowerCase() === 'r') {
            // Ctrl/Cmd + Shift + R - Refine content
            event.preventDefault();
            onAICommand?.('refine');
            return true;
        }

        if (isModifierPressed && shiftKey && key.toLowerCase() === 'g') {
            // Ctrl/Cmd + Shift + G - Generate content
            event.preventDefault();
            onAICommand?.('generate');
            return true;
        }

        if (isModifierPressed && shiftKey && key.toLowerCase() === 't') {
            // Ctrl/Cmd + Shift + T - Summarize title
            event.preventDefault();
            onAICommand?.('summarize-title');
            return true;
        }

        if (isModifierPressed && shiftKey && key.toLowerCase() === 'i') {
            // Ctrl/Cmd + Shift + I - Insert image (AI-powered)
            event.preventDefault();
            onAICommand?.('generate-image');
            return true;
        }

        return false;
    }, [editorInstance, isAgentMode, onSave, onAICommand, onOpenAiPrompt]);

    return { handleKeyboardShortcuts };
}
